// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// Export the factory function instead of a class to prevent initialization order problems

import { TotpCodesReasons } from "le-coffre-resources/dist/Customer/TotpCodes";

// Export types
export interface ICustomerTokens {
	accessToken: string;
	refreshToken: string;
}

export type IMailVerifyParams = {
	email: string;
	/** When the same email exists on several dossiers (login link with ?folderUid=). */
	folderUid?: string;
	/** When the same email exists across offices; restricts login to customers of this office. */
	officeUid?: string;
	customerUid?: string;
	/** Set to CUSTOMER after explicit choice when the email is both client and tiers (same office). */
	loginActor?: string;
};

export type DeliveryChannel = "sms" | "email";

export type IMailVerifyReturn = {
	deliveryChannel?: DeliveryChannel;
	partialPhoneNumber?: string | null;
	maskedEmail?: string | null;
	totpCodeUid?: string;
	needsChoice?: boolean;
	actorChoiceRequired?: boolean;
	customerCandidates?: Array<{
		customerUid: string;
		folderUid: string;
		folderNumber: string;
		officeUid: string;
		officeName: string;
		roleLabel: string;
	}>;
	thirdPartyCandidates?: Array<{
		thirdPartyUid: string;
		folderUid: string;
		folderNumber: string | null;
		officeUid: string;
		officeName: string;
		roleType: string;
	}>;
	totalChoiceRows?: number;
};

export type IVerifyTotpCodeParams = {
	totpCode: string;
	email: string;
	folderUid?: string;
	officeUid?: string;
	customerUid?: string;
	loginActor?: string;
};

export type IVerifyTotpCodeReturn = {
	validCode: boolean;
	reason: TotpCodesReasons;
};

export type ISetPasswordParams = {
	password: string;
	email: string;
	totpCode: string;
	folderUid?: string;
	officeUid?: string;
	customerUid?: string;
	loginActor?: string;
};

export type ILoginParams = {
	email: string;
	totpCode: string;
	folderUid?: string;
	officeUid?: string;
	customerUid?: string;
	loginActor?: string;
};

export type IAskNewPasswordParams = {
	email: string;
	folderUid?: string;
	officeUid?: string;
	customerUid?: string;
	loginActor?: string;
};

export type IAskAnotherCodeParams = {
	email: string;
	totpCodeUid: string;
	folderUid?: string;
	officeUid?: string;
	customerUid?: string;
	loginActor?: string;
};

export type IAskAnotherCodeReturn = {
	deliveryChannel: DeliveryChannel;
	partialPhoneNumber?: string | null;
	maskedEmail?: string | null;
	totpCodeUid: string;
};

// Export a compatible interface for backwards compatibility
export interface IAuth {
	mailVerifySms(body: IMailVerifyParams): Promise<IMailVerifyReturn>;
	verifyTotpCode(body: IVerifyTotpCodeParams): Promise<IVerifyTotpCodeReturn>;
	setPassword(body: ISetPasswordParams): Promise<ICustomerTokens>;
	login(body: ILoginParams): Promise<ICustomerTokens>;
	askNewPassword(body: IAskNewPasswordParams): Promise<IMailVerifyReturn>;
	sendAnotherCode(body: IAskAnotherCodeParams): Promise<IAskAnotherCodeReturn>;
	logout(): Promise<{ message: string }>;
}

import { getLazyFactoryGetter } from "@Front/Api/helpers/dynamicFactoryLoader";

function getFactory(): () => IAuth {
	return getLazyFactoryGetter("Auth/Customer") as () => IAuth;
}

// Export the instance getter as the main export
export const Auth = {
	getInstance: (): IAuth => {
		return getFactory()();
	},
};

// Export default for backward compatibility with existing imports
export default Auth;
